import React, {
  ChangeEvent,
  KeyboardEvent,
  memo,
  RefObject,
  useEffect,
  useRef,
  useState,
  createRef,
} from 'react'
import { FieldErrors } from 'react-hook-form'

import styled from '@emotion/styled'
import { isPlainObject } from 'is-plain-object'

import { isEmptyOrNil } from '@surglogs/util'

import { useHandler } from '@surglogs-app/hooks'
import { StatusActive, StatusExpired } from '@surglogs-app/icons'
import { Box, FlexBox, Icon, Text } from '@surglogs-app/ui'
import {
  INVERSE_SEQUENCE,
  SEQUENCE,
} from '@surglogs-app/util/src/constants/Pin'

import { getConditionColor } from '../helpers/getConditionColor'
import { useSecurityPin } from '../hooks/useSecurityPin'
import { useAccountSettingsTranslation } from '../services'

const StyledContainer = styled(FlexBox)`
  gap: ${({ theme }) => theme.ui.spacings.sm};

  & input {
    display: inline-block;
    width: ${({ theme }) => theme.ui.spacings.xxl};
    text-align: center;
  }
`

export interface PinFormGroupProps {
  component: React.NamedExoticComponent<
    Pick<any, string | number | symbol> & React.RefAttributes<HTMLInputElement>
  >
  valuesArray?: string[]
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  withDescription?: boolean
  errors: FieldErrors<any>
}

interface PinInputConditionsProps {
  isSequence?: { value: boolean; example: string }
  isRepetitive?: { value: boolean; example: string }
}

const getValue = (e: Event[]) => {
  if (isPlainObject(e)) {
    try {
      return e[0]
    } catch (error) {
      return e
    }
  }

  return e
}

export const PinFormGroup = memo(
  ({
    component: Component,
    valuesArray = undefined,
    errors,
    ...rest
  }: PinFormGroupProps) => {
    const { isSequenceExample, isRepetitiveExample, pinArray } =
      useSecurityPin()
    const refs: RefObject<any> = useRef(
      pinArray.map(() => createRef<HTMLInputElement>()),
    )
    const { t } = useAccountSettingsTranslation()
    const [isSequence, setIsSequence] = useState(false)
    const [isRepetitive, setIsRepetitive] = useState(false)
    const conditions: PinInputConditionsProps = {
      isSequence: {
        value: isSequence,
        example: isSequenceExample,
      },
      isRepetitive: {
        value: isRepetitive,
        example: isRepetitiveExample,
      },
    }
    const pins = pinArray

    const validate = useHandler(() => {
      const pinValuesArray =
        valuesArray ?? refs.current.map((r: any) => r?.current?.value)
      const value = pinValuesArray.join('')

      if (value.length === pins.length) {
        if (
          !SEQUENCE.includes(String(value)) &&
          !INVERSE_SEQUENCE.includes(String(value))
        ) {
          setIsSequence(true)
        } else {
          setIsSequence(false)
        }

        if ([...new Set(pinValuesArray)].length === 1) {
          setIsRepetitive(false)
        } else {
          setIsRepetitive(true)
        }
      } else {
        setIsSequence(false)
        setIsRepetitive(false)
      }
    })

    const focusInputOnIndex = useHandler((index: number) => {
      const sanitizedIndex = Math.max(
        Math.min(refs.current.length - 1, index),
        0,
      )
      try {
        ;(refs.current[sanitizedIndex].current as HTMLInputElement).focus()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('---next pin focus has failed', error)
      }
    })

    const onChange = useHandler((index: number) => (e: Event[]) => {
      const value = getValue(e)
      const inc = value ? +1 : 0
      const nextIndex = +index + inc

      if (!valuesArray) {
        validate()
      }

      focusInputOnIndex(nextIndex)
    })

    const onKeyDown = useHandler(
      (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' || e.key === 'Delete') {
          if (refs.current[index].current.value === '') {
            focusInputOnIndex(index - 1)
          }
          return
        }

        if (refs.current[index].current.value) {
          focusInputOnIndex(index + 1)
        }
      },
    )

    useEffect(() => {
      validate()
    }, [valuesArray, validate])

    const hasError = !isEmptyOrNil(errors)

    return (
      <Box>
        <StyledContainer>
          {Component
            ? pins.map((index) => {
                return (
                  <Component
                    key={index}
                    {...rest}
                    {...{
                      ref: refs.current[index],
                      index,
                      errors,
                      maxLength: 1,
                      onChange: onChange(index),
                      onKeyDown: onKeyDown(index),
                    }}
                  />
                )
              })
            : null}
        </StyledContainer>
        <Box {...{ role: 'group', mt: 'md', 'aria-label': 'pin-conditions' }}>
          {Object.keys(conditions).map((key) => {
            const condition = conditions[key as keyof PinInputConditionsProps]

            return (
              <FlexBox key={key} {...{ ai: 'center', mt: 'md' }}>
                <FlexBox
                  {...{
                    ai: 'center',
                    gap: 'sm',
                    color: getConditionColor(
                      condition?.value ?? false,
                      hasError,
                    ),
                  }}>
                  <Icon
                    {...{
                      icon: condition?.value ? StatusActive : StatusExpired,
                      size: 'md',
                      color: 'inherit',
                    }}
                  />
                  <Text {...{ color: 'inherit' }}>
                    {t(key as any)} {condition?.example}
                  </Text>
                </FlexBox>
              </FlexBox>
            )
          })}
        </Box>
      </Box>
    )
  },
)
